import React from 'react';
import {gettext} from '../../translations';
import Icon from 'eventbrite_design_system/icon/Icon';
import Button from 'eventbrite_design_system/button/Button';
import ChevronDownChunkySvg from 'eventbrite_design_system/iconography/icons/ChevronDownChunky';
import {withRouter} from 'react-router-dom';

import './FilterSection.scss';

class FilterSection extends React.PureComponent {
    state = {
        // the currently selected filter in this section
        selectedFilter: Object.keys(this.props.filters)[0],

        // show the whole filter section (vs collapsing part of it)
        expand: false,
    }

    componentDidMount() {
        // when the page is loaded for the first time, we check the url to see whether
        // a filter should be set in the radio buttons
        this._parseUrl(this.props.location.pathname, false);
    }

    componentDidUpdate(prevProps) {
        // if the user clicks the back or forward button, we check the new url to see
        // if a filter needs to be applied
        if (this.props.location !== prevProps.location) {
            this._parseUrl(this.props.location.pathname, true);
        }
    }

    /**
     * Examine the url and apply any filters that are present in the url
     *
     * @param {string} url - the url of this page
     * @param {bool} shouldSearch - should we make an ajax search request to the server
     *
     */
    _parseUrl = (url, shouldSearch) => {
        const {urlParameterMapping} = this.props;

        if (urlParameterMapping) {
            const urlSplit = url.split('/');

            if (urlSplit.length > 3) {
                // individual filters are separated by double dashes
                const urlParams = urlSplit[3].split('--');

                // check every possible filter to see whether it exists in this url
                for (const key in urlParameterMapping) {
                    if (urlParams.includes(key)) {
                        this._handleOptionChange(urlParameterMapping[key], shouldSearch, false);

                        // only one filter at a time so return when we find one
                        return;
                    }
                }

                // if we can't find any filters in the url, we apply the 'all' filter
                this._handleOptionChange('all', shouldSearch, false);
            }
        }
    }

    /**
     * Handle a change of the filter setting. This is triggered in three cases.
     *
     * 1. The initial page load. We want to set the radio value but not trigger a new search
     * because the server has already included the search results in the html.
     *
     * 2. The back or forward button is pressed. We want to set the radio button and trigger
     * a new search but we don't want to update the url path location.
     *
     * 3. The user has selected a new radio option.  We want to set the radio button, trigger
     * a new search, and update the url path to represent the new option state.
     *
     * @param {string} value - the new selected value to be set for the radio buttons
     * @param {bool} shouldSearch - should we make an ajax search request to the server
     * @param {bool} shouldUpdateRoute - should we set a new location in the url
     *
     */
    _handleOptionChange = (value, shouldSearch, shouldUpdateRoute) => {
        const {onOptionChange} = this.props;

        if (value !== this.state.selectedFilter) {
            this.setState({selectedFilter: value});
            if (shouldSearch) {
                onOptionChange(value, shouldUpdateRoute);
            }
        }
    };

    /**
     * Toggle the expand state specifying whether the whole filter section should be shown
     */
    _handleToggleExpand = () => {
        this.setState((prevState) => ({expand: !prevState.expand}));
    }

    render() {
        const {
            // the text title of this section of filters
            title,

            // the filter options to be presented to a user
            filters,

            // the text to present on the more button.  e.g. "More categories"
            moreLabel,

            // the number of options to present before we hide remaining options behind a "more" button
            moreThreshold,
        } = this.props;

        // if we're not in an "expand" state, we only show the first x filters (x = moreThreshold)
        const entries = Object.entries(filters);
        const filtersCropped = this.state.expand ? entries : entries.slice(0, moreThreshold);

        const radios = filtersCropped.map(([key, label]) => (
            <label
                className={`filter-section__radio filter-section__radio-${this.state.selectedFilter === key ? 'selected' : 'unselected'}`}
                key={key}
            >
                <input
                    type="radio"
                    value={key}
                    checked={this.state.selectedFilter === key}
                    onChange={() => this._handleOptionChange(key, true, true)}
                />
                <div>{label}</div>
            </label>
        ));

        return (
            <div>
                <Button
                    type="button"
                    style="none"
                    disabled={!moreThreshold}
                    onClick={this._handleToggleExpand}
                    aria-label={gettext('Expand or collapse option list')}
                    __containerClassName="filter-section__title"
                >
                    {title}
                </Button>

                {radios}
                {!this.state.expand && moreThreshold &&
                <Button
                    type="button"
                    style="none"
                    onClick={this._handleToggleExpand}
                    aria-label={gettext('Show more options')}
                    __containerClassName="filter-section__more"
                >
                    {moreLabel}
                    <Icon type={<ChevronDownChunkySvg />} color="grey-700" />
                </Button>
                }
            </div>);
    }
}

// withRouter adds a location field to props which allows us to watch for url changes
export default withRouter(FilterSection);
